/**
 * National Day Green Cap offer: the Locked/Unlocked panel on the National Day 96 T-shirt page, the offer module on
 * eligible Green Cap pages, the "Choose a Green Cap" picker and the T-shirt size picker (both also opened from the
 * cart drawer).
 *
 * The panel is Unlocked only when Shopify's own cart (/cart.js) holds an eligible cap, never from local state. It
 * re-reads the cart on load, after every cart change the theme announces, when the tab regains focus and when the
 * dialog opens. Adding a cap follows Horizon's own path (cart add with the cart section ids, then a CartAddEvent) so
 * the drawer re-renders and opens as it does for the main Add to cart button. Nothing here applies a discount: the
 * 79 SAR off the T-shirt is a native Shopify automatic Buy X Get Y.
 */
import { CartAddEvent, ThemeEvents } from '@theme/events';

if (!window.__dzNdcapBound) {
  window.__dzNdcapBound = true;

  const OFFER_TITLE = 'National Day Green Cap Offer';
  const TEE_HANDLE = 'national-day-96-loose-fit-tee';
  const panel = () => document.querySelector('[data-dz-ndcap]');
  const capPanel = () => document.querySelector('[data-dz-ndcap-cap]');
  const anyPanel = () => panel() || capPanel();

  function capIds() {
    return new Set((anyPanel()?.dataset.capIds || '').split(',').filter(Boolean));
  }

  function cartJsUrl() {
    const base = anyPanel()?.dataset.cartUrl || window.Theme?.routes?.cart_url || '/cart';
    return `${base.replace(/\/$/, '')}.js`;
  }

  /**
   * Reads the live cart. Returns { hasCap, hasTee, applied } or null when the cart could not be read. `applied` is
   * true only when Shopify itself has allocated the offer to a T-shirt line.
   */
  async function readCart() {
    const ids = capIds();
    try {
      const cart = await (await fetch(cartJsUrl(), { headers: { Accept: 'application/json' }, cache: 'no-store' })).json();
      const items = (cart.items || []).filter((item) => item.quantity > 0);
      return {
        hasCap: ids.size ? items.some((item) => ids.has(String(item.product_id))) : false,
        hasTee: items.some((item) => item.handle === TEE_HANDLE),
        applied: items.some(
          (item) =>
            item.handle === TEE_HANDLE &&
            (item.line_level_discount_allocations || []).some((a) => a.discount_application?.title === OFFER_TITLE)
        ),
      };
    } catch {
      return null;
    }
  }

  let refreshing = null;
  function refreshState() {
    if (refreshing) return refreshing;
    refreshing = (async () => {
      const cart = await readCart();
      if (cart) {
        const tee = panel();
        if (tee) tee.dataset.state = cart.hasCap ? 'unlocked' : 'locked';
        const cap = capPanel();
        if (cap) {
          cap.dataset.state = cart.applied ? 'applied' : cart.hasTee && cart.hasCap ? 'eligible' : cart.hasCap ? 'available' : 'locked';
        }
      }
      refreshing = null;
    })();
    return refreshing;
  }

  /** @param {HTMLDialogElement} dialog */
  async function loadDialogProducts(dialog) {
    if (dialog.dataset.loaded === 'true' || dialog.dataset.loading === 'true') return;
    const grid = dialog.querySelector('[data-dz-ndcap-grid]');
    const status = dialog.querySelector('[data-dz-ndcap-status]');
    const section = dialog.dataset.section;
    if (!grid || !section) return;
    dialog.dataset.loading = 'true';
    try {
      const root = dialog.dataset.root || '/';
      const base = `${root}${root.endsWith('/') ? '' : '/'}`;
      const response = await fetch(`${base}?sections=${encodeURIComponent(section)}`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(String(response.status));
      const html = (await response.json())[section] || '';
      const template = document.createElement('template');
      template.innerHTML = html;
      const list = template.content.querySelector('.dz-ndcap-list');
      if (!list || !list.querySelector('[data-dz-ndcap-card]')) throw new Error('empty');
      grid.replaceChildren(list);
      dialog.dataset.loaded = 'true';
    } catch {
      if (status) status.textContent = status.dataset.error || '';
    } finally {
      grid.setAttribute('aria-busy', 'false');
      delete dialog.dataset.loading;
    }
  }

  class CartError extends Error {}

  /** @param {HTMLElement} card @param {HTMLButtonElement} button */
  async function addToCart(card, button) {
    const field = card.querySelector('[data-dz-ndcap-variant]');
    const id = field instanceof HTMLSelectElement || field instanceof HTMLInputElement ? field.value : '';
    return addVariant(id, button, card.querySelector('[data-dz-ndcap-error]'), card.dataset.productId, card.closest('dialog'));
  }

  /**
   * Adds one unit of a variant through Horizon's own cart path at its normal price.
   * @param {string} id @param {HTMLButtonElement} button @param {HTMLElement|null} error @param {string} [productId]
   * @param {Element|null} [dialog] - the picker to close on success
   */
  async function addVariant(id, button, error, productId, dialog) {
    if (!id || button.getAttribute('aria-busy') === 'true') return;

    button.setAttribute('aria-busy', 'true');
    button.textContent = button.dataset.busyLabel || button.textContent;
    if (error) error.hidden = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(id), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      // Shopify's own cart errors (for example, not enough stock) come back already in the storefront language.
      if (!response.ok || data.status) throw new CartError(data.description || data.message || '');

      // cart-icon.js treats itemCount from any source other than the product form as the cart's total.
      let itemCount = 1;
      try {
        const cart = await (await fetch(cartJsUrl(), { headers: { Accept: 'application/json' }, cache: 'no-store' })).json();
        if (typeof cart.item_count === 'number') itemCount = cart.item_count;
      } catch {}

      if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();

      // Unlocked comes from re-reading the cart, not from the add having succeeded.
      await refreshState();
      document.dispatchEvent(
        new CartAddEvent({}, `dz-ndcap-${id}`, {
          source: 'dz-ndcap',
          itemCount,
          variantId: String(id),
          productId,
          sections: data.sections,
        })
      );
    } catch (failure) {
      if (error) {
        error.textContent = (failure instanceof CartError && failure.message) || button.dataset.error || '';
        error.hidden = false;
      }
    } finally {
      if (button.isConnected) {
        button.removeAttribute('aria-busy');
        button.textContent = button.dataset.label || button.textContent;
      }
    }
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const teeOpener = target.closest('[data-dz-ndcap-open-tee]');
    if (teeOpener) {
      const dialog =
        document.querySelector('body > dialog[data-dz-ndcap-tee-dialog]') ||
        document.querySelector('dialog[data-dz-ndcap-tee-dialog]');
      if (dialog instanceof HTMLDialogElement) {
        if (dialog.parentElement !== document.body) document.body.append(dialog);
        // Every opening starts with no size chosen, even if this dialog was used before on the same page.
        document.querySelectorAll('dialog[data-dz-ndcap-tee-dialog]:not(body > dialog)').forEach((extra) => extra.remove());
        dialog.querySelectorAll('input[data-dz-ndcap-size]').forEach((input) => (input.checked = false));
        const add = dialog.querySelector('[data-dz-ndcap-add-tee]');
        const hint = dialog.querySelector('[data-dz-ndcap-tee-hint]');
        const error = dialog.querySelector('[data-dz-ndcap-error]');
        if (add instanceof HTMLButtonElement) add.disabled = true;
        if (hint) hint.hidden = false;
        if (error) error.hidden = true;
        dialog.showModal();
        refreshState();
      }
      return;
    }

    const addTee = target.closest('[data-dz-ndcap-add-tee]');
    if (addTee instanceof HTMLButtonElement) {
      const dialog = addTee.closest('dialog[data-dz-ndcap-tee-dialog]');
      const picked = dialog?.querySelector('input[data-dz-ndcap-size]:checked');
      const error = dialog?.querySelector('[data-dz-ndcap-error]');
      if (!(picked instanceof HTMLInputElement)) {
        // The shopper must choose a size; nothing is ever pre-selected or added on their behalf.
        if (error) {
          error.textContent = addTee.dataset.needsSize || '';
          error.hidden = !error.textContent;
        }
        return;
      }
      addVariant(picked.value, addTee, error, dialog?.querySelector('[data-dz-ndcap-tee]')?.dataset.productId, dialog);
      return;
    }

    const opener = target.closest('[data-dz-ndcap-open]');
    if (opener) {
      // One dialog serves every opener. Prefer a copy already lifted to <body> (it keeps its loaded cards), then the
      // copy next to the opener (product page), then any copy (cart drawer re-renders bring a fresh one each time).
      const sibling = opener.closest('.dz-ndcap')?.nextElementSibling;
      const dialog =
        document.querySelector('body > dialog[data-dz-ndcap-dialog]') ||
        (sibling?.matches?.('dialog[data-dz-ndcap-dialog]') ? sibling : null) ||
        document.querySelector('dialog[data-dz-ndcap-dialog]');
      if (dialog instanceof HTMLDialogElement) {
        // Inside the cart drawer the dialog sits in another <dialog> and in a region the theme re-renders on every
        // cart change, which would tear it down mid-use. Lift it to <body> and keep it there.
        if (dialog.parentElement !== document.body) document.body.append(dialog);
        dialog.showModal();
        loadDialogProducts(dialog);
        refreshState();
      }
      return;
    }

    if (target.closest('[data-dz-ndcap-close]')) {
      target.closest('dialog')?.close();
      return;
    }

    // A click on the backdrop lands on the dialog element itself.
    if (target instanceof HTMLDialogElement && target.matches('[data-dz-ndcap-dialog], [data-dz-ndcap-tee-dialog]')) {
      target.close();
      return;
    }

    const add = target.closest('[data-dz-ndcap-add]');
    const card = add?.closest('[data-dz-ndcap-card]');
    if (add instanceof HTMLButtonElement && card instanceof HTMLElement) addToCart(card, add);
  });

  document.addEventListener('change', (event) => {
    const size = event.target;
    if (size instanceof HTMLInputElement && size.matches('input[data-dz-ndcap-size]')) {
      const dialog = size.closest('dialog');
      const button = dialog?.querySelector('[data-dz-ndcap-add-tee]');
      const hint = dialog?.querySelector('[data-dz-ndcap-tee-hint]');
      const error = dialog?.querySelector('[data-dz-ndcap-error]');
      if (button instanceof HTMLButtonElement) button.disabled = false;
      if (hint) hint.hidden = true;
      if (error) error.hidden = true;
      return;
    }
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-dz-ndcap-variant]')) return;
    const price = select.closest('[data-dz-ndcap-card]')?.querySelector('[data-dz-ndcap-price]');
    const option = select.selectedOptions[0];
    if (price && option?.dataset.price) price.textContent = option.dataset.price;
  });

  // Every cart change the theme announces (drawer quantity edits, removals, other add buttons) re-reads the cart.
  document.addEventListener(ThemeEvents.cartUpdate, () => {
    refreshState();
    for (const kind of ['data-dz-ndcap-dialog', 'data-dz-ndcap-tee-dialog']) {
      if (document.querySelector(`body > dialog[${kind}]`)) {
        document.querySelectorAll(`dialog[${kind}]:not(body > dialog)`).forEach((extra) => extra.remove());
      }
    }
  });
  document.addEventListener(ThemeEvents.cartError, () => refreshState());
  window.addEventListener('pageshow', () => refreshState());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshState();
  });
  refreshState();
}
